import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { PlatformAdminInfo, PlatformAdminRole } from '@/types/platformAdmin';

/**
 * Hook to detect and retrieve platform admin status and permissions
 * Caches result for 10 minutes to minimize database calls
 */
export function usePlatformAdmin() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['platform-admin', user?.id],
    queryFn: async (): Promise<PlatformAdminInfo> => {
      if (!user?.id) {
        return {
          isAdmin: false,
          adminRole: null,
          permissions: {} as any
        };
      }

      const { data, error } = await supabase
        .rpc('get_platform_admin_info')
        .single();
      
      if (error) {
        throw error;
      }
      
      return {
        isAdmin: data.is_admin || false,
        adminRole: data.admin_role as PlatformAdminRole,
        permissions: (data.permissions || {}) as any
      };
    },
    enabled: !!user?.id,
    staleTime: 10 * 60 * 1000, // 10 minutes - admin status doesn't change frequently
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

/**
 * Hook to check a specific platform permission
 * @param permission - Permission key to check (e.g., 'can_access_all_orgs')
 */
export function usePlatformPermission(permission: string) {
  const { data: adminInfo, isLoading } = usePlatformAdmin();
  
  return {
    hasPermission: adminInfo?.permissions?.[permission] || false,
    isLoading
  };
}
